import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  untracked,
} from '@angular/core';

import moment from 'moment-timezone';

import { AdminsAvatarsComponent } from '@core/account-ui';
import type { InterviewEventWidget } from '@core/dashboard-types';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import {
  getFormattedInterval,
  getFormatterDate,
  interviewEventName,
} from '@core/interview-schedule-util';
import type { JobItem } from '@core/offers-types';
import { OfferStatusIndicatorComponent } from '@core/offers-ui';
import type { TalentPoolBrief } from '@core/talent-pools-types';
import { BadgeComponent, DividerComponent, IconComponent } from '@core/user-interface-legacy';

import { InterviewEventParticipantsComponent } from '../interview-event-participants/interview-event-participants.component';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'rt-dashboard-event-item',
  imports: [
    InterviewEventParticipantsComponent,
    AdminsAvatarsComponent,
    OfferStatusIndicatorComponent,
    DividerComponent,
    IconComponent,
    BadgeComponent,
    TranslatePipe,
  ],
  templateUrl: 'dashboard-event-item.component.html',
  styleUrl: 'dashboard-event-item.component.less',
})
export class DashboardEventItemComponent {
  private readonly i18n = inject(I18nService);

  public event = input.required<InterviewEventWidget>();
  public hasEventCustomName = input.required<boolean>();
  public currentUserId = input.required<number>();
  public isNew = input<boolean>();

  public openOffer = output<JobItem | TalentPoolBrief>();
  public openCandidate = output<{ id: number }>();

  protected currentUserIsInterviewer = computed(() => {
    const currentUserId = this.currentUserId();
    return this.event().adminIds.some(adminId => adminId === currentUserId);
  });
  protected hasEvaluation = computed(
    () => this.event().interviewResultRequests && this.event().interviewResultRequests.length > 0,
  );
  protected hasComment = computed(() => !!this.event().note || !!this.event().publicNote);
  protected eventName = computed(() => {
    const event = this.event();
    const hasEventCustomName = this.hasEventCustomName();
    return untracked(() =>
      hasEventCustomName && event.customName
        ? event.customName
        : interviewEventName(event, this.i18n),
    );
  });
  protected interval = computed(() => {
    const event = this.event();
    return untracked(() => getFormattedInterval(moment(event.startsAt), event));
  });
  protected date = computed(() => {
    const event = this.event();
    return untracked(() => getFormatterDate(event.startsAt));
  });

  protected onNavigateToOffer(event: MouseEvent, offerOrTp: JobItem | TalentPoolBrief) {
    event.stopPropagation();

    this.openOffer.emit(offerOrTp);
  }

  protected openCandidateModal(event: MouseEvent, id: number) {
    event.stopPropagation();

    this.openCandidate.emit({ id });
  }
}
